import React from 'react';
import { ImageIcon } from '@revfluence/fresh-icons/solid/esm';
import { ISocialPost } from '@components';
import {
  ContentPreviewMultiCard,
  ContentPreviewMultiCardMedia,
  ContentPreviewImageGrid,
  ContentPreviewImage,
} from '@/shadcn/components/content-preview';

interface ProspectPostsGridProps {
  posts: ISocialPost[];
  isLoading: boolean;
  username: string;
}

export const ProspectPostsGrid: React.FC<ProspectPostsGridProps> = ({
  posts,
  isLoading,
  username,
}) => {
  // Transform ISocialPost to flat props format for ContentPreviewImageGrid
  const transformToFlatProps = (posts: ISocialPost[]) => {
    const validPosts = posts.filter((post): post is ISocialPost => post !== null);

    if (validPosts.length === 0) {
      return {
        images: [],
      };
    }

    // Extract images from posts, prioritizing video thumbnail over image URL
    const images: ContentPreviewImage[] = validPosts
      .filter((post) => post.image || post.video)
      .map((post, index) => ({
        id: post.id || `post-${index}`,
        url: post.image || post.video || '',
        alt: `${username} post ${index + 1}`,
      }));

    return {
      images,
    };
  };

  const { images } = transformToFlatProps(posts);

  const handleImageClick = (image: ContentPreviewImage) => {
    // Find the corresponding post for this image
    const correspondingPost = posts.find((post) => post.id === image.id || `post-${posts.indexOf(post)}` === image.id);

    if (correspondingPost?.link) {
      // Open the post link in a new tab
      window.open(correspondingPost.link, '_blank', 'noopener,noreferrer');
    } else if (image.url) {
      // Fallback to image URL if no link is available
      window.open(image.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ImageIcon className="h-4 w-4 text-grey-6" />
        <h3 className="text-sm font-medium text-grey-12">Recent Posts</h3>
      </div>

      <ContentPreviewMultiCard isLoading={isLoading}>
        <ContentPreviewMultiCardMedia className="no-gradient">
          <ContentPreviewImageGrid
            images={images}
            onImageClick={handleImageClick}
          />
        </ContentPreviewMultiCardMedia>
      </ContentPreviewMultiCard>
    </div>
  );
};
